"use client";

import { useMemo, useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

// Libellés embarqués plutôt que passés par next-intl : la démo est insérée
// depuis le contenu HTML du terme, qui n'a pas accès aux clés de traduction.
// Le contenu français pose data-lang="fr", l'anglais data-lang="en".
const L = {
    fr: {
        title: "Estimez votre facture mensuelle",
        calls: "Appels par mois",
        input: "Entrée par appel (mots)",
        output: "Sortie par appel (mots)",
        model: "Modèle",
        monthly: "Coût mensuel estimé",
        breakdown: "dont",
        inLabel: "entrée",
        outLabel: "sortie",
        tokens: "jetons par mois",
        note: "Tarifs indicatifs par million de jetons, à vérifier chez votre fournisseur. Le calcul convertit les mots en jetons au ratio de 4 pour 3.",
        cheap: "Petit",
        mid: "Intermédiaire",
        large: "Grand",
    },
    en: {
        title: "Estimate your monthly bill",
        calls: "Calls per month",
        input: "Input per call (words)",
        output: "Output per call (words)",
        model: "Model",
        monthly: "Estimated monthly cost",
        breakdown: "of which",
        inLabel: "input",
        outLabel: "output",
        tokens: "tokens per month",
        note: "Indicative rates per million tokens, check with your provider. The calculation converts words to tokens at a ratio of 4 to 3.",
        cheap: "Small",
        mid: "Mid-range",
        large: "Large",
    },
};

// Ordres de grandeur du marché, volontairement ronds : la démo sert à montrer
// l'écart entre les tiers, pas à afficher le tarif exact d'un fournisseur.
const MODELS = [
    { key: "cheap", in: 0.3, out: 1.5 },
    { key: "mid", in: 3, out: 15 },
    { key: "large", in: 15, out: 75 },
];

const WORDS_TO_TOKENS = 4 / 3;

export default function CostCalculator({ lang = "fr" }) {
    const t = L[lang] || L.fr;

    const [calls, setCalls] = useState(500);
    const [inputWords, setInputWords] = useState(2000);
    const [outputWords, setOutputWords] = useState(200);
    const [model, setModel] = useState(1);

    const result = useMemo(() => {
        const inTokens = calls * inputWords * WORDS_TO_TOKENS;
        const outTokens = calls * outputWords * WORDS_TO_TOKENS;
        const rate = MODELS[model];

        const inCost = (inTokens / 1_000_000) * rate.in;
        const outCost = (outTokens / 1_000_000) * rate.out;

        return {
            inTokens,
            outTokens,
            inCost,
            outCost,
            total: inCost + outCost,
        };
    }, [calls, inputWords, outputWords, model]);

    const money = (value) =>
        value < 1
            ? `${value.toFixed(2).replace(".", lang === "fr" ? "," : ".")} $`
            : `${Math.round(value).toLocaleString(lang === "fr" ? "fr-FR" : "en-US")} $`;

    const big = (value) =>
        Math.round(value).toLocaleString(lang === "fr" ? "fr-FR" : "en-US");

    const field = (label, value, setValue, min, max, step) => (
        <div>
            <div className="flex justify-between items-baseline mb-1.5">
                <label className="text-sm text-gray-600 dark:text-lightBlue mb-0">
                    {label}
                </label>
                <span className="text-sm font-bold tabular-nums dark:text-white">
                    {big(value)}
                </span>
            </div>
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(e) => setValue(Number(e.target.value))}
                className="w-full accent-primary cursor-pointer"
            />
        </div>
    );

    return (
        <div className="my-8 rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] p-5 sm:p-6 text-base leading-normal">
            <div className="flex items-center gap-2 mb-5">
                <SolarIcon
                    name="calculator-minimalistic-linear"
                    className="w-5 h-5 text-primary"
                />
                <div className="font-bold dark:text-white text-base leading-snug">
                    {t.title}
                </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-4">
                    {field(t.calls, calls, setCalls, 10, 20000, 10)}
                    {field(t.input, inputWords, setInputWords, 50, 20000, 50)}
                    {field(t.output, outputWords, setOutputWords, 20, 3000, 20)}

                    <div>
                        <div className="text-sm text-gray-600 dark:text-lightBlue mb-1.5">
                            {t.model}
                        </div>
                        <div className="flex gap-1.5">
                            {MODELS.map((m, i) => (
                                <button
                                    key={m.key}
                                    type="button"
                                    onClick={() => setModel(i)}
                                    className={`flex-1 px-2 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                                        model === i
                                            ? "bg-primary text-white border-primary"
                                            : "border-gray-200 dark:border-white/10 text-gray-600 dark:text-lightBlue hover:border-gray-300 dark:hover:border-white/20"
                                    }`}
                                >
                                    {t[m.key]}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="flex flex-col justify-center rounded-xl bg-primary/[0.06] dark:bg-primary/10 p-5">
                    <div className="text-sm text-gray-600 dark:text-lightBlue mb-0">
                        {t.monthly}
                    </div>
                    <div className="text-4xl font-bold text-primary tabular-nums my-1">
                        {money(result.total)}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-lightBlue mb-0">
                        {t.breakdown} {money(result.inCost)} {t.inLabel} ·{" "}
                        {money(result.outCost)} {t.outLabel}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-lightBlue/70 mt-3 mb-0 tabular-nums">
                        {big(result.inTokens + result.outTokens)} {t.tokens}
                    </div>
                </div>
            </div>

            <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-4 mb-0">
                {t.note}
            </p>
        </div>
    );
}
